import {
  Children,
  cloneElement,
  ComponentPropsWithoutRef,
  CSSProperties,
  isValidElement,
  ReactNode
} from 'react'
import clsx from 'clsx'
import { LinkTypeSchema } from 'cms/schemaTypes/generalSchemas/linkType'

import { getCMSLinkParams } from 'lib/sanity/links'
import Link from 'shared/ui/Link/Link'
import { purgeWidowsAndOrphans } from 'shared/utils/common'

import styles from './shared.module.scss'

export const serialize =
  (Component: React.ComponentType<{ children?: ReactNode }>) =>
  (props: { children?: ReactNode }) => {
    const isEmptyString = (child: ReactNode) =>
      typeof child === 'string' && child.trim() === ''

    if (
      Array.isArray(props.children) &&
      props.children.length === 1 &&
      isEmptyString(props.children[0] as ReactNode)
    ) {
      return <br />
    }

    return <Component {...props} />
  }

export const formatText = (children: ReactNode): ReactNode => {
  return Children.map(children, (child: ReactNode) => {
    if (typeof child === 'string') {
      return purgeWidowsAndOrphans(child)
    }

    if (isValidElement<{ children?: ReactNode }>(child)) {
      const subChildren = child.props.children

      const formattedSubChildren = Children.map(subChildren, subChild => {
        if (typeof subChild === 'string') {
          return purgeWidowsAndOrphans(subChild)
        }

        return subChild
      })

      return cloneElement(child, undefined, formattedSubChildren)
    }

    return child
  })
}

export const formatAndSerialize = (
  Component: React.ComponentType<{ children?: ReactNode }>
) => {
  return ({ children }: { children?: ReactNode }) => {
    const formatted = formatText(children)

    return serialize(Component)({ children: formatted })
  }
}

const getRichTextColor = (value: RichTextColorValue): string => {
  if (typeof value === 'string') {
    return value
  }

  return value?.color?.hex || value?.colorReference?.color || 'inherit'
}

export const LeftDecorator = (props: { children: ReactNode }) => {
  return <span className={styles.alignLeft}>{props.children}</span>
}

export const CenterDecorator = (props: { children: ReactNode }) => {
  return <span className={styles.alignCenter}>{props.children}</span>
}

export const RightDecorator = (props: { children: ReactNode }) => {
  return <span className={styles.alignRight}>{props.children}</span>
}

export type RichTextAlignmentValue = { align?: 'left' | 'center' | 'right' }

export const renderAlignedChildren = (
  children: ReactNode,
  value?: RichTextAlignmentValue
) => {
  if (value?.align === 'right') {
    return RightDecorator({ children })
  }

  if (value?.align === 'center') {
    return CenterDecorator({ children })
  }

  return LeftDecorator({ children })
}

export type RichTextColorValue =
  | { color?: { hex?: string }; colorReference?: { color?: string } }
  | string

export const ColorDecorator = (props: {
  children: ReactNode
  value: RichTextColorValue
}) => {
  const color = getRichTextColor(props.value)

  const style = { '--rich-text-color': color } as CSSProperties

  return (
    <span className={styles.colorDecorator} style={style}>
      {props.children}
    </span>
  )
}

export const StyledStrong = ({
  className,
  ...props
}: ComponentPropsWithoutRef<'strong'>) => (
  <strong className={clsx(styles.strong, className)} {...props} />
)

export const TextStyleH1 = ({
  className,
  ...props
}: ComponentPropsWithoutRef<'h1'>) => (
  <h1 className={clsx(styles.textStyleH1, className)} {...props} />
)

export const TextStyleH2 = ({
  className,
  ...props
}: ComponentPropsWithoutRef<'h2'>) => (
  <h2 className={clsx(styles.textStyleH2, className)} {...props} />
)

export const TextStyleH3 = ({
  className,
  ...props
}: ComponentPropsWithoutRef<'h3'>) => (
  <h3 className={clsx(styles.textStyleH3, className)} {...props} />
)

export const TextStyleH4 = ({
  className,
  ...props
}: ComponentPropsWithoutRef<'h4'>) => (
  <h4 className={clsx(styles.textStyleH4, className)} {...props} />
)

export const TextStyleH5 = ({
  className,
  ...props
}: ComponentPropsWithoutRef<'h5'>) => (
  <h5 className={clsx(styles.textStyleH5, className)} {...props} />
)

export const TextStyleH6 = ({
  className,
  ...props
}: ComponentPropsWithoutRef<'h6'>) => (
  <h6 className={clsx(styles.textStyleH6, className)} {...props} />
)

export const TextStyleNano = ({
  className,
  ...props
}: ComponentPropsWithoutRef<'p'>) => (
  <p className={clsx(styles.textStyleNano, className)} {...props} />
)

export const TextStyleMicro = ({
  className,
  ...props
}: ComponentPropsWithoutRef<'p'>) => (
  <p className={clsx(styles.textStyleMicro, className)} {...props} />
)

export const TextStyleSmall = ({
  className,
  ...props
}: ComponentPropsWithoutRef<'p'>) => (
  <p className={clsx(styles.textStyleSmall, className)} {...props} />
)

export const TextStyleBase = ({
  className,
  ...props
}: ComponentPropsWithoutRef<'p'>) => (
  <p className={clsx(styles.textStyleBase, className)} {...props} />
)

export const TextStyleLarge = ({
  className,
  ...props
}: ComponentPropsWithoutRef<'p'>) => (
  <p className={clsx(styles.textStyleLarge, className)} {...props} />
)

export const TextStyleBlockquote = ({
  className,
  ...props
}: ComponentPropsWithoutRef<'blockquote'>) => (
  <blockquote
    className={clsx(styles.textStyleBlockquote, className)}
    {...props}
  />
)

export const StyledUlList = ({
  className,
  ...props
}: ComponentPropsWithoutRef<'ul'>) => (
  <ul className={clsx(styles.ulList, className)} {...props} />
)

export const StyledOlList = ({
  className,
  ...props
}: ComponentPropsWithoutRef<'ol'>) => (
  <ol className={clsx(styles.olList, className)} {...props} />
)

export const LinkDecorator = (props: {
  children: ReactNode
  value: { link?: LinkTypeSchema }
}) => {
  const link =
    'value' in props && 'link' in props.value ? props.value?.link : null

  if (!link) {
    return <>{props.children}</>
  }

  const getSanityLinkParams = getCMSLinkParams(link)

  return (
    <Link
      href={getSanityLinkParams.href || ''}
      target='_blank'
      rel='noopener noreferrer'
    >
      {props.children}
    </Link>
  )
}
